import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Calculator, FileText, TrendingUp } from "lucide-react";

const translations = {
  cnr: {
    label: "CG",
    nav: "Crnogorski",
    badge: "Sajt u izradi",
    headline: "Gradimo nešto",
    headlineAccent: "pouzdano.",
    subtitle:
      "Naša nova web stranica je u pripremi. Uskoro ćemo predstaviti kompletnu ponudu računovodstvenih usluga i poreskog savjetovanja.",
    services: "Naše usluge",
    s1: "Računovodstvo i knjigovodstvo",
    s2: "Poresko savjetovanje",
    s3: "Finansijsko izvještavanje",
    contactTitle: "Kontaktirajte nas već danas",
    soon: "Lansiranje uskoro",
    email: "Email",
    phone: "Telefon",
    address: "Adresa",
    rights: "Sva prava zadržana.",
  },
  sq: {
    label: "AL",
    nav: "Shqip",
    badge: "Faqja në ndërtim",
    headline: "Po ndërtojmë diçka",
    headlineAccent: "të besueshme.",
    subtitle:
      "Faqja jonë e re po përgatitet. Së shpejti do të prezantojmë ofertën e plotë të shërbimeve të kontabilitetit dhe këshillimit tatimor.",
    services: "Shërbimet tona",
    s1: "Kontabilitet dhe libërmbajtje",
    s2: "Këshillim tatimor",
    s3: "Raportim financiar",
    contactTitle: "Na kontaktoni qysh sot",
    soon: "Lansimi së shpejti",
    email: "Email",
    phone: "Telefon",
    address: "Adresa",
    rights: "Të gjitha të drejtat e rezervuara.",
  },
  en: {
    label: "EN",
    nav: "English",
    badge: "Site under construction",
    headline: "We're building something",
    headlineAccent: "trustworthy.",
    subtitle:
      "Our new website is on its way. Soon we'll showcase our full range of accounting services and tax advisory.",
    services: "Our services",
    s1: "Accounting & bookkeeping",
    s2: "Tax advisory",
    s3: "Financial reporting",
    contactTitle: "Get in touch today",
    soon: "Launching soon",
    email: "Email",
    phone: "Phone",
    address: "Address",
    rights: "All rights reserved.",
  },
};

const LANGS = ["cnr", "sq", "en"];

// Brand palette — Classic professional: deep navy blue + white + brand blue accent
const NAVY = "#0a2748";          // primary background
const NAVY_DEEP = "#061a33";     // deeper layer
const BRAND_BLUE = "#3a8dde";    // accent (sampled from logo)
const BRAND_BLUE_SOFT = "#7fb4ea";

export default function ComingSoon() {
  const [lang, setLang] = useState("cnr");
  const t = translations[lang];

  useEffect(() => {
    document.title = "FINANCE GROUP — " + translations[lang].badge;
  }, [lang]);

  const services = useMemo(
    () => [
      { icon: Calculator, text: t.s1 },
      { icon: FileText, text: t.s2 },
      { icon: TrendingUp, text: t.s3 },
    ],
    [t]
  );

  return (
    <div
      data-testid="coming-soon-page"
      className="relative min-h-screen w-full overflow-hidden text-white"
      style={{
        fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
        background: `linear-gradient(180deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)`,
      }}
    >
      {/* Subtle grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Ambient blue glow */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full opacity-[0.18] blur-[120px]"
        style={{ background: BRAND_BLUE }}
      />
      <div
        className="pointer-events-none absolute -bottom-60 -left-40 h-[600px] w-[600px] rounded-full opacity-50 blur-[120px]"
        style={{ background: "#0e3870" }}
      />

      {/* Top bar */}
      <header className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-7 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="brand-logo"
          className="flex items-center"
        >
          <img
            src="/finance-group-logo-light.png"
            alt="FINANCE GROUP"
            className="h-14 w-auto md:h-16"
          />
        </motion.div>

        {/* Lang switch */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="language-switcher"
          className="flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] p-1 backdrop-blur"
        >
          {LANGS.map((code) => (
            <button
              key={code}
              data-testid={`lang-${code}`}
              onClick={() => setLang(code)}
              className={`relative rounded-full px-4 py-1.5 text-xs font-medium tracking-wider transition-colors duration-300 ${
                lang === code ? "text-[#0a2748]" : "text-white/70 hover:text-white"
              }`}
              style={{ fontFamily: "ui-sans-serif, system-ui" }}
            >
              {lang === code && (
                <motion.div
                  layoutId="lang-pill"
                  className="absolute inset-0 rounded-full bg-white"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative">{translations[code].label}</span>
            </button>
          ))}
        </motion.div>
      </header>

      {/* Main content */}
      <main className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-16 px-6 pb-20 pt-10 md:grid-cols-12 md:px-12 md:pt-16">
        {/* Left: Hero */}
        <div className="md:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={lang}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
            >
              <div
                data-testid="badge"
                className="inline-flex items-center gap-2 border-l-2 pl-3 text-[11px] uppercase tracking-[0.32em]"
                style={{
                  fontFamily: "ui-sans-serif, system-ui",
                  borderColor: BRAND_BLUE_SOFT,
                  color: BRAND_BLUE_SOFT,
                }}
              >
                <span className="relative flex h-2 w-2">
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                    style={{ background: BRAND_BLUE_SOFT }}
                  ></span>
                  <span
                    className="relative inline-flex h-2 w-2 rounded-full"
                    style={{ background: BRAND_BLUE_SOFT }}
                  ></span>
                </span>
                {t.badge}
              </div>

              <h1
                data-testid="headline"
                className="mt-8 text-[44px] leading-[0.95] tracking-tight text-white md:text-[88px]"
              >
                {t.headline}
                <br />
                <span className="italic" style={{ color: BRAND_BLUE_SOFT }}>
                  {t.headlineAccent}
                </span>
              </h1>

              <p
                data-testid="subtitle"
                className="mt-8 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
                style={{ fontFamily: "ui-sans-serif, system-ui" }}
              >
                {t.subtitle}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Services preview */}
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-white/15 bg-white/10 sm:grid-cols-3">
            {services.map((s, i) => (
              <motion.div
                key={i + lang}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                data-testid={`service-${i}`}
                className="group p-5 transition-colors duration-500"
                style={{ background: NAVY }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0f3360")}
                onMouseLeave={(e) => (e.currentTarget.style.background = NAVY)}
              >
                <s.icon
                  size={22}
                  strokeWidth={1.4}
                  className="transition-transform duration-500 group-hover:-translate-y-0.5"
                  style={{ color: BRAND_BLUE_SOFT }}
                />
                <p
                  className="mt-4 text-sm leading-snug text-white/85"
                  style={{ fontFamily: "ui-sans-serif, system-ui" }}
                >
                  {s.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right: contact card */}
        <div className="md:col-span-5">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative overflow-hidden rounded-sm border border-white/15 p-8 md:p-10"
            style={{
              background: `linear-gradient(160deg, #0f3360 0%, ${NAVY_DEEP} 100%)`,
            }}
          >
            <div
              className="absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl"
              style={{ background: `${BRAND_BLUE}33` }}
            />

            <div
              data-testid="soon-tag"
              className="text-[10px] uppercase tracking-[0.4em]"
              style={{
                fontFamily: "ui-sans-serif, system-ui",
                color: BRAND_BLUE_SOFT,
              }}
            >
              — {t.soon}
            </div>

            <h2 className="mt-4 text-3xl text-white md:text-4xl">
              {t.contactTitle}
            </h2>

            <div
              className="mt-10 space-y-6"
              style={{ fontFamily: "ui-sans-serif, system-ui" }}
            >
              <ContactRow
                icon={Phone}
                label={t.phone}
                value={
                  <div className="space-y-0.5">
                    <a
                      href="tel:+38230412012"
                      className="block hover:text-white transition-colors"
                      style={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      +382 (0) 30 412 012
                    </a>
                    <a
                      href="tel:+38267229119"
                      className="block hover:text-white transition-colors"
                      style={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      +382 (0) 67 229 119
                    </a>
                    <a
                      href="tel:+38269033898"
                      className="block hover:text-white transition-colors"
                      style={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      +382 (0) 69 033 898
                    </a>
                  </div>
                }
                testid="contact-phone"
              />
              <ContactRow
                icon={Mail}
                label={t.email}
                value="info@financegroup.me"
                testid="contact-email"
                href="mailto:info@financegroup.me"
              />
              <ContactRow
                icon={MapPin}
                label={t.address}
                value="Bulevar Skenderbeu BB, Ulcinj"
                testid="contact-address"
              />
            </div>

            {/* Decorative progress */}
            <div className="mt-12">
              <div
                className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-white/50"
                style={{ fontFamily: "ui-sans-serif, system-ui" }}
              >
                <span>Progress</span>
                <span>72%</span>
              </div>
              <div className="mt-3 h-px w-full overflow-hidden bg-white/15">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "72%" }}
                  transition={{ duration: 1.8, delay: 0.6, ease: "easeOut" }}
                  className="h-full"
                  style={{
                    background: `linear-gradient(90deg, ${BRAND_BLUE} 0%, #ffffff 100%)`,
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 mx-auto w-full max-w-7xl border-t border-white/10 px-6 py-6 md:px-12">
        <div
          className="flex flex-col items-start justify-between gap-2 text-[11px] tracking-wider text-white/50 md:flex-row md:items-center"
          style={{ fontFamily: "ui-sans-serif, system-ui" }}
        >
          <div>
            © {new Date().getFullYear()} FINANCE GROUP DOO — {t.rights}
          </div>
          <div className="uppercase tracking-[0.3em]">
            {translations.cnr.nav} · {translations.sq.nav} · {translations.en.nav}
          </div>
        </div>
      </footer>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div
        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border"
        style={{
          borderColor: "rgba(127,180,234,0.35)",
          background: "rgba(127,180,234,0.08)",
        }}
      >
        <Icon size={15} strokeWidth={1.6} style={{ color: BRAND_BLUE_SOFT }} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
          {label}
        </div>
        <div className="mt-1 text-sm text-white/90">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a
      data-testid={testid}
      href={href}
      className="block transition-opacity hover:opacity-80"
    >
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
